/**
 * T1 — 동일 스캔 후보 묶음: `관찰(스캔) 1개 → 후보 N개`.
 *
 * per-scan 동정은 한 스캔에서 top-k 후보를 내므로 features 에는 같은 스캔
 * (저장 RT 동일)에서 나온 행이 여러 개 있다. 후보 행을 독립 검출로 세거나
 * 서로를 지지하는 증거로 보면 증거가 인위적으로 중복된다(기획 v4 §3-①,
 * 전문가 패널 최상위 위험). 화면은 후보를 관찰 단위로 묶어 보여야 하고,
 * 같은 관찰의 후보는 질의 스펙트럼이 같으므로 참조 순위도 같다.
 *
 * 백엔드 `/features` 가 observation_id·candidate_rank 를 준다. 레거시/목업
 * 데이터에는 없으므로, 없으면 후보 하나를 단독 관찰로 본다(기존 동작 유지).
 */
import type { FeatureData } from "@/types/spectrum";
import {
  type CompoundCategory,
  classifyCompound,
} from "@/utils/compoundCategory";

export interface Observation {
  /** 관찰 id — 백엔드 observation_id("O3") 또는 레거시 fallback 의 feature_id. */
  id: string;
  /** 관찰(스캔) RT. */
  rt: number;
  /** 관찰 번호 (peak_no). 레거시 fallback 은 feature 의 peak_no. */
  peakNo: number;
  /** rt 의 출처 (백엔드 rt_source). 없으면 undefined. */
  rtSource?: string;
  /** 이 관찰의 후보들 — 관찰 내 순위(candidate_rank) 오름차순. */
  features: FeatureData[];
  /** 후보 수 (= features.length). */
  size: number;
}

/**
 * 후보별 관찰 키. 백엔드 observation_id 가 있으면 그것, 없으면(레거시/목업)
 * feature_id — 단, 관찰 필드 없이 feature_id 가 겹치는 입력은 합치지 않는다
 * (오래된 픽스처가 전부 "F1" 인 경우가 있다; 근거 없이 묶는 쪽이 더 위험하다).
 */
function observationKeys(features: FeatureData[]): string[] {
  const seen = new Set<string>();
  return features.map((feature, index) => {
    if (feature.observation_id != null) return feature.observation_id;
    const key = seen.has(feature.feature_id)
      ? `${feature.feature_id}#${index}`
      : feature.feature_id;
    seen.add(feature.feature_id);
    return key;
  });
}

function byCandidateRank(a: FeatureData, b: FeatureData): number {
  const ra = a.candidate_rank ?? Number.MAX_SAFE_INTEGER;
  const rb = b.candidate_rank ?? Number.MAX_SAFE_INTEGER;
  return ra - rb;
}

/**
 * 후보 목록을 관찰 단위로 묶는다. 관찰 순서는 입력에서 처음 등장한 순서,
 * 관찰 안의 후보는 candidate_rank 오름차순(없으면 입력 순서 유지).
 */
export function groupFeaturesByObservation(
  features: FeatureData[],
): Observation[] {
  const order: string[] = [];
  const buckets = new Map<string, FeatureData[]>();
  const keys = observationKeys(features);
  features.forEach((feature, index) => {
    const key = keys[index];
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(feature);
    } else {
      buckets.set(key, [feature]);
      order.push(key);
    }
  });
  return order.map((key) => {
    const members = [...(buckets.get(key) ?? [])].sort(byCandidateRank);
    const head = members[0];
    return {
      id: key,
      rt: head.rt,
      peakNo: head.peak_no,
      rtSource: head.rt_source,
      features: members,
      size: members.length,
    };
  });
}

/**
 * 관찰마다 대표 후보(관찰 내 1순위) 하나씩. TIC 수직선·MZRT 색상·범례처럼
 * "스캔 하나 = 표시 하나" 여야 하는 차트의 단위다 — 같은 스캔 후보 5개가
 * 같은 x 에 선 5개를 겹쳐 그리지 않게 한다.
 */
export function representativeFeatures(features: FeatureData[]): FeatureData[] {
  return groupFeaturesByObservation(features).map((obs) => obs.features[0]);
}

/** 관찰 수와 후보 수를 구분해 센다 — "검출 N건" 을 후보 수로 세지 않기 위함. */
export function countObservations(features: FeatureData[]): {
  observationCount: number;
  candidateCount: number;
} {
  const keys = new Set(observationKeys(features));
  return { observationCount: keys.size, candidateCount: features.length };
}

export interface CategoryObservationCount {
  /** 이 카테고리 후보를 하나라도 가진 관찰(스캔) 수. */
  observations: number;
  /** 이 카테고리로 분류된 후보 행 수. */
  candidates: number;
}

/**
 * 카테고리별 **관찰 수**와 후보 수를 따로 센다 — "방향족 5건" 이 사실은 한
 * 스캔의 Ethylbenzene·Xylene 3종·p-Nitrotoluene 인 경우를 관찰 1건으로 세기 위함.
 * 한 관찰이 여러 카테고리의 후보를 가지면 각 카테고리에 1씩 센다 (어느 쪽인지
 * 미정이므로). 정렬: 관찰 수 내림차순 → 후보 수 내림차순 (insertion 유지).
 */
export function aggregateCategoriesByObservation(
  features: FeatureData[],
): Map<CompoundCategory, CategoryObservationCount> {
  const counts = new Map<CompoundCategory, CategoryObservationCount>();
  for (const observation of groupFeaturesByObservation(features)) {
    const categoriesInObservation = new Set<CompoundCategory>();
    for (const feature of observation.features) {
      const category = classifyCompound(
        feature.smiles,
        feature.predicted_substance,
      );
      const entry = counts.get(category) ?? { observations: 0, candidates: 0 };
      entry.candidates += 1;
      counts.set(category, entry);
      categoriesInObservation.add(category);
    }
    for (const category of categoriesInObservation) {
      const entry = counts.get(category);
      if (entry) entry.observations += 1;
    }
  }
  const sorted = new Map<CompoundCategory, CategoryObservationCount>();
  Array.from(counts.entries())
    .sort(
      (a, b) =>
        b[1].observations - a[1].observations ||
        b[1].candidates - a[1].candidates,
    )
    .forEach(([category, entry]) => sorted.set(category, entry));
  return sorted;
}

/** feature_id 로 그 후보가 속한 관찰을 찾는다. */
export function findObservationOf(
  features: FeatureData[],
  featureId: string | null | undefined,
): Observation | null {
  if (!featureId) return null;
  const index = features.findIndex((x) => x.feature_id === featureId);
  if (index < 0) return null;
  const key = observationKeys(features)[index];
  return (
    groupFeaturesByObservation(features).find((obs) => obs.id === key) ?? null
  );
}

/** 같은 스캔의 다른 후보(형제)를 관찰 내 순위순으로 돌려준다. 자기 자신은 제외. */
export function siblingCandidates(
  features: FeatureData[],
  feature: FeatureData,
): FeatureData[] {
  const obs = findObservationOf(features, feature.feature_id);
  if (!obs) return [];
  return obs.features.filter((x) => x.feature_id !== feature.feature_id);
}
